import type { ExternalProjectImportPreview } from '@provision/entity-types';

export type TotalImportCountResult = {
  projects: string;
  clients: string;
  sites: string;
};

/**
 * Formats a DtoolsProjectImportPreview as a short summary string.
 * Example: 'Created: project, client • Updated: site (Address 1, Zip), client (Primary Email)'
 */
export function projectImportToString({
  created = [],
  updated = [],
}: ExternalProjectImportPreview): string {
  const parts: string[] = [];
  if (created.length > 0) {
    parts.push(`Created: ${created.map((e) => e.entity.toLowerCase()).join(', ')}`);
  }

  if (updated.length > 0) {
    const updatedStr = updated
      .map((e) => {
        const label = e.entity.toLowerCase();
        const fields = (e.diffs ?? []).map((d) => d.field).join(', ');
        return fields ? `${label} (${fields})` : label;
      })
      .join(', ');
    parts.push(`Updated: ${updatedStr}`);
  }

  return parts.length > 0 ? parts.join(' • ') : 'No entity changes detected';
}

/**
 * Aggregates created/updated counts across records per entity type.
 * Example: { projects: '1 created', clients: '1 created, 2 updated', sites: '1 created, 1 updated' }
 */
export function totalImportCount(
  records: ExternalProjectImportPreview[],
): TotalImportCountResult {
  const counts: Record<'Project' | 'Site' | 'Client', { created: number; updated: number }> = {
    Project: { created: 0, updated: 0 },
    Site: { created: 0, updated: 0 },
    Client: { created: 0, updated: 0 },
  };

  const entityKeys = ['Project', 'Site', 'Client'] as const;
  for (const record of records) {
    for (const e of record.created ?? []) {
      if (e?.entity && entityKeys.includes(e.entity)) {
        counts[e.entity].created += 1;
      }
    }
    for (const e of record.updated ?? []) {
      if (e?.entity && entityKeys.includes(e.entity)) {
        counts[e.entity].updated += 1;
      }
    }
  }

  const formatPart = (created: number, updated: number): string => {
    const parts: string[] = [];
    if (created > 0) parts.push(`${created} created`);
    if (updated > 0) parts.push(`${updated} updated`);
    return parts.join(', ') || '0';
  };

  return {
    projects: formatPart(counts.Project.created, counts.Project.updated),
    clients: formatPart(counts.Client.created, counts.Client.updated),
    sites: formatPart(counts.Site.created, counts.Site.updated),
  };
}
